import { makeAutoObservable, runInAction } from 'mobx';
import docsAgent, { WikiEntry, isDocsRequestCanceled } from '../services/api/docsAgent';

export type WikiEntryStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface WikiEntryState {
  status: WikiEntryStatus;
  data?: WikiEntry;
  error?: unknown;
  fetchedAt?: number;
}

interface LoadOptions {
  force?: boolean;
  signal?: AbortSignal;
}

const DEFAULT_ENTRY_TTL_MS = 3 * 60 * 60 * 1000;
const ENTRY_REHYDRATE_MS = 3 * 24 * 60 * 60 * 1000;
const STORAGE_PREFIX = 'docsStore.entry.';
const STORAGE_VERSION = 1;

export default class DocsStore {
  entries = new Map<string, WikiEntryState>();
  private inflightLoads = new Map<string, Promise<WikiEntry | undefined>>();
  wikiEntries: WikiEntry[] = [];
  wikiEntriesStatus: WikiEntryStatus = 'idle';
  wikiEntriesError?: unknown;
  wikiEntriesFetchedAt?: number;
  private wikiEntriesLoad?: Promise<WikiEntry[] | undefined>;
  entryTtlMs = DEFAULT_ENTRY_TTL_MS;

  constructor() {
    makeAutoObservable(this);
  }

  getEntry = (tooltipId: string) => {
    const key = tooltipId.trim();
    if (!key) {
      return undefined;
    }
    return this.entries.get(key) ?? this.hydrateEntryFromStorage(key);
  };

  private storageAvailable = () => {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  };

  private buildStorageKey = (tooltipId: string) => `${STORAGE_PREFIX}${tooltipId}`;

  private persistEntry = (entry: WikiEntry, fetchedAt: number) => {
    if (!this.storageAvailable()) {
      return;
    }
    const key = entry.tooltipId.trim();
    if (!key) {
      return;
    }
      const payload = {
        version: STORAGE_VERSION,
        fetchedAt,
        data: {
          tooltipId: key,
          url: entry.url,
          contentText: entry.contentText,
          contentImage: entry.contentImage ?? undefined,
          lastUpdatedAt: entry.lastUpdatedAt,
        },
      };
    try {
      window.localStorage.setItem(this.buildStorageKey(key), JSON.stringify(payload));
    } catch (error) {
      console.warn('Failed to persist wiki entry', error);
    }
  };

  private hydrateEntryFromStorage = (tooltipId: string) => {
    if (!this.storageAvailable()) {
      return undefined;
    }
    try {
      const raw = window.localStorage.getItem(this.buildStorageKey(tooltipId));
      if (!raw) {
        return undefined;
      }
      const parsed = JSON.parse(raw) as {
        version: number;
        fetchedAt?: number;
          data?: {
            tooltipId?: string;
            url?: string;
            contentText?: string;
            contentImage?: string;
            lastUpdatedAt?: string;
          };
      };
      if (parsed.version !== STORAGE_VERSION || !parsed.data?.tooltipId || parsed.data.tooltipId !== tooltipId) {
        return undefined;
      }
      if (!parsed.data.url || !parsed.data.contentText || !parsed.data.lastUpdatedAt) {
        return undefined;
      }
      const fetchedAt = parsed.fetchedAt ?? Date.now();
      const hydrated: WikiEntryState = {
        status: 'ready',
          data: {
            wikiEntryID: 0,
            contentHTML: '',
            tooltipId,
            url: parsed.data.url,
            contentText: parsed.data.contentText,
            contentImage: parsed.data.contentImage,
            lastUpdatedAt: parsed.data.lastUpdatedAt,
          },
        fetchedAt,
      };
      this.entries.set(tooltipId, hydrated);
      return hydrated;
    } catch (error) {
      console.warn('Failed to hydrate wiki entry from storage', error);
      return undefined;
    }
  };

  private isStale = (entry?: WikiEntryState) => {
    if (!entry || !entry.fetchedAt) {
      return true;
    }
    if (Date.now() - entry.fetchedAt > this.entryTtlMs) {
      return true;
    }
    return this.isEntryOutdated(entry.data);
  };

  private isEntryOutdated = (data?: WikiEntry) => {
    if (!data?.lastUpdatedAt) {
      return true;
    }
    const lastUpdated = Date.parse(data.lastUpdatedAt);
    if (Number.isNaN(lastUpdated)) {
      return true;
    }
    return Date.now() - lastUpdated >= ENTRY_REHYDRATE_MS;
  };

  private isListStale = () => {
    if (!this.wikiEntriesFetchedAt) {
      return true;
    }
    return Date.now() - this.wikiEntriesFetchedAt > this.entryTtlMs;
  };

  private cacheEntry = (entry: WikiEntry, previousTooltipId?: string) => {
    const key = entry.tooltipId.trim();
    if (!key) {
      return;
    }
    const prevKey = previousTooltipId?.trim();
    const normalizedEntry = key === entry.tooltipId ? entry : { ...entry, tooltipId: key };
    if (prevKey && prevKey !== key) {
      this.entries.delete(prevKey);
    }
    const fetchedAt = Date.now();
    const state: WikiEntryState = {
      status: 'ready',
      data: normalizedEntry,
      fetchedAt,
    };
    this.entries.set(key, state);
    this.persistEntry(normalizedEntry, fetchedAt);
  };

  load = async (tooltipId: string, options: LoadOptions = {}) => {
    const key = tooltipId.trim();
    if (!key) {
      return undefined;
    }

    let existing = this.entries.get(key);
    if (!existing) {
      existing = this.hydrateEntryFromStorage(key);
    }
    const { force = false, signal } = options;

    if (!force && existing?.status === 'ready' && existing.data && !this.isStale(existing)) {
      return existing.data;
    }

    if (!force && this.inflightLoads.has(key)) {
      return this.inflightLoads.get(key);
    }

    runInAction(() => {
      this.entries.set(key, {
        status: 'loading',
        data: existing?.data,
        fetchedAt: existing?.fetchedAt,
      });
    });

    const loadPromise = docsAgent.WikiEntries.getByTooltipId(key, signal)
      .then((entry) => {
        runInAction(() => {
          this.cacheEntry(entry, existing?.data?.tooltipId);
        });
        return entry;
      })
      .catch((error) => {
        if (isDocsRequestCanceled(error)) {
        runInAction(() => {
          if (existing) {
            this.entries.set(key, existing);
          } else {
            this.entries.delete(key);
          }
        });
          return undefined;
        }

        runInAction(() => {
          this.entries.set(key, {
            status: 'error',
            data: existing?.data,
            fetchedAt: Date.now(),
            error,
          });
        });
        throw error;
      })
      .finally(() => {
        runInAction(() => {
          this.inflightLoads.delete(key);
        });
      });

    this.inflightLoads.set(key, loadPromise);

    return loadPromise;
  };

  listEntries = async (options: LoadOptions = {}) => {
    const { force = false, signal } = options;
    if (!force && this.wikiEntriesStatus === 'ready' && !this.isListStale()) {
      return this.wikiEntries;
    }
    if (!force && this.wikiEntriesLoad) {
      return this.wikiEntriesLoad;
    }

    runInAction(() => {
      this.wikiEntriesStatus = 'loading';
      this.wikiEntriesError = undefined;
    });

    const loadPromise = docsAgent.WikiEntries.list(undefined, signal)
      .then((entries) => {
        runInAction(() => {
          this.wikiEntries = entries;
          this.wikiEntriesStatus = 'ready';
          this.wikiEntriesFetchedAt = Date.now();
          entries.forEach((entry) => this.cacheEntry(entry));
        });
        return entries;
      })
      .catch((error) => {
        if (isDocsRequestCanceled(error)) {
          runInAction(() => {
            this.wikiEntriesStatus = this.wikiEntries.length ? 'ready' : 'idle';
          });
          return undefined;
        }
        runInAction(() => {
          this.wikiEntriesError = error;
          this.wikiEntriesStatus = 'error';
          this.wikiEntriesFetchedAt = Date.now();
        });
        throw error;
      })
      .finally(() => {
        runInAction(() => {
          this.wikiEntriesLoad = undefined;
        });
      });

    this.wikiEntriesLoad = loadPromise;
    return loadPromise;
  };

  createEntry = async (
    payload: { url: string; tooltipId: string; lastUpdatedAt?: string },
    signal?: AbortSignal,
  ) => {
    const entry = await docsAgent.WikiEntries.create(payload, signal);
    runInAction(() => {
      this.wikiEntries = [entry, ...this.wikiEntries];
      this.wikiEntriesStatus = 'ready';
      this.wikiEntriesFetchedAt = Date.now();
      this.cacheEntry(entry);
    });
    return entry;
  };

  updateEntry = async (id: number, payload: Partial<Omit<WikiEntry, 'wikiEntryID'>>, signal?: AbortSignal) => {
    const existing = this.wikiEntries.find((entry) => entry.wikiEntryID === id);
    const updated = await docsAgent.WikiEntries.update(id, payload, signal);
    runInAction(() => {
      this.wikiEntries = this.wikiEntries.map((entry) => (entry.wikiEntryID === id ? updated : entry));
      this.cacheEntry(updated, existing?.tooltipId);
    });
    return updated;
  };

  forceUpdateEntries = async (payload: { url: string; tooltipIds?: string[] }, signal?: AbortSignal) => {
    const updatedEntries = await docsAgent.WikiEntries.forceUpdate(payload, signal);
    runInAction(() => {
      if (!updatedEntries.length) {
        return;
      }
      const updatesById = new Map(updatedEntries.map((entry) => [entry.wikiEntryID, entry]));
      this.wikiEntries = this.wikiEntries.map((entry) => {
        const updated = updatesById.get(entry.wikiEntryID);
        if (!updated) {
          return entry;
        }
        updatesById.delete(entry.wikiEntryID);
        this.cacheEntry(updated, entry.tooltipId);
        return updated;
      });
      updatesById.forEach((entry) => {
        this.wikiEntries.push(entry);
        this.cacheEntry(entry);
      });
      this.wikiEntriesStatus = 'ready';
      this.wikiEntriesFetchedAt = Date.now();
    });
    return updatedEntries;
  };

  refresh = async (tooltipId: string, signal?: AbortSignal) => {
    return this.load(tooltipId, { force: true, signal });
  };

  preload = (tooltipIds: string[]) => {
    return Promise.all(tooltipIds.map((name) => this.load(name)));
  };

  clear = () => {
    runInAction(() => {
      this.entries.clear();
      this.inflightLoads.clear();
      this.wikiEntries = [];
      this.wikiEntriesStatus = 'idle';
      this.wikiEntriesError = undefined;
      this.wikiEntriesFetchedAt = undefined;
      this.wikiEntriesLoad = undefined;
    });
  };
}
